import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, UploadCloud, Camera, CheckCircle2, Circle, X } from "lucide-react";

function StatRow({ label, ok, testid }) {
  return (
    <div data-testid={testid} className="flex items-center justify-between border-b border-slate-100 py-2.5 last:border-b-0">
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className={`inline-flex items-center gap-1.5 text-sm font-semibold ${ok ? "text-emerald-700" : "text-slate-500"}`}>
        {ok ? <CheckCircle2 className="h-4 w-4" strokeWidth={2.25} /> : <Circle className="h-4 w-4" strokeWidth={2} />}
        {ok ? "Completed" : "Pending"}
      </div>
    </div>
  );
}

function UploadIdModal({ workerId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/workers/${workerId}/identity/upload-id`, { filename: file?.name || "government_id.jpg" });
      toast.success("Government ID uploaded");
      onDone?.();
      onClose?.();
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="upload-id-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1221]/70 p-6" onClick={onClose}>
      <div className="relative w-full max-w-md border border-slate-200 bg-white p-7" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center text-slate-400 hover:text-[#0B1221]" type="button" data-testid="upload-id-close">
          <X className="h-4 w-4" />
        </button>
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">Identity · step 1</div>
        <h3 className="font-display mt-1 text-2xl font-bold tracking-tight text-[#0B1221]">Upload Government ID</h3>
        <p className="mt-2 text-sm text-slate-600">Driver&apos;s license, state ID, or passport. MVP build — file upload is simulated.</p>

        <label className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center hover:border-[#0B1221]" data-testid="upload-id-dropzone">
          <UploadCloud className="h-8 w-8 text-slate-400" strokeWidth={1.75} />
          <div className="text-sm font-semibold text-[#0B1221]">{file ? file.name : "Select ID file"}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">JPG · PNG · PDF</div>
          <input
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            data-testid="upload-id-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>

        <div className="mt-5 flex gap-3">
          <button type="button" onClick={onClose} data-testid="upload-id-cancel" className="flex-1 border border-slate-300 bg-white px-4 py-3 text-sm font-semibold uppercase tracking-wider text-[#0B1221] hover:border-[#0B1221]">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="upload-id-submit" className="flex-1 bg-[#FF5A1F] px-4 py-3 text-sm font-semibold uppercase tracking-wider text-white hover:bg-[#e94d14] disabled:opacity-60">
            {busy ? "Uploading…" : "Mark Uploaded"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SelfieModal({ workerId, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/workers/${workerId}/identity/selfie`);
      toast.success("Selfie check completed");
      onDone?.();
      onClose?.();
    } catch {
      toast.error("Selfie check failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="selfie-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1221]/70 p-6" onClick={onClose}>
      <div className="relative w-full max-w-md border border-slate-200 bg-white p-7" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center text-slate-400 hover:text-[#0B1221]" type="button" data-testid="selfie-close">
          <X className="h-4 w-4" />
        </button>
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">Identity · step 2</div>
        <h3 className="font-display mt-1 text-2xl font-bold tracking-tight text-[#0B1221]">Take Selfie</h3>
        <p className="mt-2 text-sm text-slate-600">Look at the camera frame. MVP build — biometric matching is not enabled in this release.</p>

        <div className="relative mt-5 flex aspect-square items-center justify-center overflow-hidden border border-slate-200 bg-slate-900" data-testid="selfie-viewport">
          <div className="absolute h-48 w-48 rounded-full border-2 border-dashed border-[#FF5A1F]/80" />
          <Camera className="h-10 w-10 text-white/80" strokeWidth={1.5} />
          <div className="absolute bottom-2 left-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/70">MVP · simulated capture</div>
        </div>

        <div className="mt-5 flex gap-3">
          <button type="button" onClick={onClose} data-testid="selfie-cancel" className="flex-1 border border-slate-300 bg-white px-4 py-3 text-sm font-semibold uppercase tracking-wider text-[#0B1221] hover:border-[#0B1221]">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="selfie-submit" className="flex-1 bg-[#FF5A1F] px-4 py-3 text-sm font-semibold uppercase tracking-wider text-white hover:bg-[#e94d14] disabled:opacity-60">
            {busy ? "Capturing…" : "Capture Selfie"}
          </button>
        </div>
      </div>
    </div>
  );
}

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${m}/${day}/${y}`;
};

export default function IdentityPanel({ workerId, onStatusChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showSelfie, setShowSelfie] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/workers/${workerId}/identity`).then((r) => {
      setData(r.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [workerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (data && onStatusChange) onStatusChange(data.status);
  }, [data, onStatusChange]);

  if (loading || !data) {
    return <div data-testid="identity-panel-loading" className="border border-slate-200 bg-white p-5 text-sm text-slate-500">Loading identity…</div>;
  }

  const verified = data.status === "ProCred Identity Verified";

  return (
    <div data-testid="identity-panel" className="relative border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">Identity Verification</div>
          <div className="mt-1 flex items-center gap-2">
            <ShieldCheck className={`h-4 w-4 ${verified ? "text-[#FF5A1F]" : "text-slate-400"}`} strokeWidth={2.5} />
            <span data-testid="identity-status" className={`font-display text-base font-bold ${verified ? "text-[#0B1221]" : "text-slate-600"}`}>
              {data.status}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-slate-500">
            Verified on: <span data-testid="identity-verified-on" className="font-semibold text-[#0B1221]">{fmtDate(data.verified_on)}</span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            data-testid="identity-upload-id-btn"
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#0B1221] hover:border-[#0B1221]"
          >
            <UploadCloud className="h-3.5 w-3.5" /> Upload ID
          </button>
          <button
            type="button"
            data-testid="identity-selfie-btn"
            onClick={() => setShowSelfie(true)}
            className="inline-flex items-center gap-2 bg-[#0B1221] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#1E293B]"
          >
            <Camera className="h-3.5 w-3.5" /> Take Selfie
          </button>
        </div>
      </div>

      <div className="mt-4 border-t border-slate-100 pt-2">
        <StatRow label="Government ID Uploaded" ok={data.gov_id_uploaded} testid="identity-row-gov-id" />
        <StatRow label="Identity Selfie Captured" ok={data.selfie_completed} testid="identity-row-selfie" />
        <StatRow label="Identity Verified by ProCred" ok={verified} testid="identity-row-procred" />
      </div>

      {showUpload && <UploadIdModal workerId={workerId} onClose={() => setShowUpload(false)} onDone={load} />}
      {showSelfie && <SelfieModal workerId={workerId} onClose={() => setShowSelfie(false)} onDone={load} />}
    </div>
  );
}
